import { useState } from 'react'

import i18n from 'i18n-literally'
import PropTypes from 'prop-types'

import { WhatsApp } from '@mui/icons-material'
import { Box, FormControlLabel, MenuItem, Radio, RadioGroup, Stack } from '@mui/material'
import { styled } from '@mui/material/styles'
import { defaultCountries, parseCountry, usePhoneInput } from 'react-international-phone'

import { useApiMutation } from '~/src/IO/Api'
import { noop } from '~/src/Lib/Utils'
import { defaultChannelFor } from '~/src/Lib/Utils/strings'
import PhoneCodeEntry from '~/src/Routes/Login/PhoneCodeEntry'
import PhoneLoginNote from '~/src/Routes/Login/PhoneLoginNote'
import { validatePhoneNumber } from '~/src/Store/bundles/me/shape'
import { useConnectModern } from '~/src/Store/hooks/useConnectModern'
import Button from '~/src/UI/Shared/Button'
import TextField from '~/src/UI/Shared/Form/TextField'
import T from '~/src/UI/Shared/Typography'

const displayName = 'PhoneLogin'

const countries = defaultCountries.filter(country => {
  const { iso2 } = parseCountry(country)
  return ['au', 'ca', 'es', 'gb', 'mx', 'pt', 'us'].includes(iso2)
})

const Title = styled(T.Body.Bold)(({ theme }) => ({
  letterSpacing: '0.12em',
  marginBottom: theme.spacing(2),
  textAlign: 'center',
  textTransform: 'uppercase',
  width: '100%',
}))

const STEPS = Object.freeze({ phone: 'phone', channel: 'channel', code: 'code' })

const DELIVERY_ERROR = i18n`We couldn't send the code. Try again.`

const Actions = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  gap: 'clamp(0.5rem, 1.5vh, 1rem)',
  marginTop: theme.spacing(3),
}))

const PhoneLogin = ({ onCancel }) => {
  const { doPhoneLogin } = useConnectModern('doPhoneLogin')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [step, setStep] = useState(STEPS.phone)
  // Left unset, the channel follows the number's country; picking one pins it.
  const [pickedChannel, setPickedChannel] = useState(null)
  const [challenge, setChallenge] = useState(null)
  const [codeError, setCodeError] = useState('')
  const [deliveryError, setDeliveryError] = useState('')
  const channel = pickedChannel ?? defaultChannelFor(phoneNumber)
  const { inputValue, country, setCountry, handlePhoneValueChange, inputRef } = usePhoneInput({
    defaultCountry: 'us',
    countries,
    forceDialCode: true,
    onChange: ({ phone, country: selected }) => {
      setPhoneNumber(phone === `+${selected.dialCode}` ? '' : phone)
    },
  })

  // The endpoint answers 202 whether or not the number belongs to an account, so
  // the code screen follows either way.
  const { mutate: requestCode, isPending: requestingCode } = useApiMutation({
    mutationFn: (payload, { apiFetch }) =>
      apiFetch('/token/sms/request/', payload, { method: 'POST', authenticated: false }),
  })

  // Resend identifies the challenge on its own; it takes no phone number.
  const { mutate: resendCode } = useApiMutation({
    mutationFn: (payload, { apiFetch }) =>
      apiFetch('/token/sms/resend/', payload, { method: 'POST', authenticated: false }),
  })

  const sendCode = () => {
    setDeliveryError('')
    requestCode(
      { phoneNumber, channel },
      {
        onSuccess: requested => {
          setChallenge(requested)
          setStep(STEPS.code)
        },
        onError: () => setDeliveryError(DELIVERY_ERROR),
      },
    )
  }

  if (step === STEPS.code) {
    return (
      <PhoneCodeEntry
        error={codeError}
        phoneNumber={phoneNumber}
        onEdit={() => setStep(STEPS.phone)}
        onComplete={async code => {
          setCodeError((await doPhoneLogin({ challengeId: challenge?.challengeId, code })) ?? '')
        }}
        onResend={() => {
          setCodeError('')
          resendCode(
            { challengeId: challenge?.challengeId },
            { onSuccess: setChallenge, onError: () => setCodeError(DELIVERY_ERROR) },
          )
        }}
        onUsePassword={onCancel}
      />
    )
  }

  if (step === STEPS.channel) {
    return (
      <Box data-testid="phone-channel-choice" sx={{ px: { xs: 0, sm: 'calc(50% - 250px)' } }}>
        <Title>{i18n`Choose how to confirm that it is you`}</Title>

        <RadioGroup
          aria-label={i18n`Where to send the code`}
          data-testid="code-channel"
          value={channel}
          onChange={event => setPickedChannel(event.target.value)}
        >
          <FormControlLabel value="sms" control={<Radio />} label={i18n`SMS`} />
          <FormControlLabel
            value="whatsapp"
            control={<Radio />}
            label={
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <WhatsApp color="success" />
                <span>{i18n`WhatsApp`}</span>
              </Stack>
            }
          />
        </RadioGroup>

        {deliveryError ? (
          <T.Tiny data-testid="delivery-error" sx={{ color: 'error.main', textAlign: 'center' }}>
            {deliveryError}
          </T.Tiny>
        ) : null}

        <Actions>
          <Button fullWidth disabled={requestingCode} data-testid="send-code-button" onClick={sendCode}>
            {i18n`Send Code`}
          </Button>
          <Button variant="text" data-testid="phone-cancel-button" onClick={onCancel}>
            {i18n`Cancel`}
          </Button>
        </Actions>
      </Box>
    )
  }

  return (
    <Box data-testid="phone-login" sx={{ px: { xs: 0, sm: 'calc(50% - 250px)' } }}>
      <Title>{i18n`Enter Phone`}</Title>

      <TextField
        fullWidth
        select
        margin="normal"
        label={i18n`Country`}
        value={country.iso2}
        onChange={iso2 => setCountry(iso2)}
        data-testid="phone-country-field"
      >
        {countries.map(item => {
          const parsed = parseCountry(item)
          return (
            <MenuItem key={parsed.iso2} value={parsed.iso2}>
              {parsed.name}
            </MenuItem>
          )
        })}
      </TextField>

      <TextField
        fullWidth
        margin="normal"
        type="tel"
        label={i18n`Phone`}
        value={inputValue}
        inputRef={inputRef}
        onChange={noop}
        inputProps={{ 'data-testid': 'phone-input', onChange: handlePhoneValueChange }}
      />

      {/* Completes the 24px gap the fields keep between them. */}
      <PhoneLoginNote sx={{ mt: 2 }} />

      <Actions>
        <Button
          fullWidth
          disabled={!validatePhoneNumber(phoneNumber)}
          data-testid="phone-next-button"
          onClick={() => setStep(STEPS.channel)}
        >
          {i18n`Next`}
        </Button>
        <Button variant="text" data-testid="phone-cancel-button" onClick={onCancel}>
          {i18n`Cancel`}
        </Button>
      </Actions>
    </Box>
  )
}

PhoneLogin.displayName = displayName
PhoneLogin.propTypes = {
  onCancel: PropTypes.func.isRequired,
}

export default PhoneLogin
